import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Paginated,
  Product,
  ProductType,
  ProductInput,
  ProductListQuery,
  ProductUpdate,
  RestockInput,
} from '@sirajoptic/shared';
import { api } from '@/lib/api-client';
import { cleDashboard } from '@/features/dashboard/api/dashboard.api';

export const clesProduits = {
  tous: ['produits'] as const,
  liste: (q: Partial<ProductListQuery>) => ['produits', 'liste', q] as const,
  un: (id: string) => ['produits', id] as const,
};

export function useProduits(q: Partial<ProductListQuery>) {
  return useQuery({
    queryKey: clesProduits.liste(q),
    queryFn: () => api.liste<Product>('/products', q),
    placeholderData: (precedent) => precedent,
  });
}

/**
 * Liste courte pour les sélecteurs de produit (lignes de facture, déduction,
 * postes de commande). `type` restreint aux montures, verres ou lentilles selon
 * le poste — sans lui, le champ « Monture » proposerait aussi des lentilles.
 */
export function useProduitsPourSelection(recherche: string, type?: ProductType) {
  return useQuery({
    queryKey: ['produits', 'selection', recherche, type ?? 'tous'] as const,
    queryFn: () => api.liste<Product>('/products', { q: recherche, type, limit: 20 }),
    staleTime: 60_000,
  });
}

/** Invalide tout ce que le stock influence — listes et tableau de bord. */
function useInvalider() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: clesProduits.tous });
    void client.invalidateQueries({ queryKey: cleDashboard });
    void client.invalidateQueries({ queryKey: ['mouvements'] });
  };
}

export function useCreerProduit() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (input: ProductInput) => api.post<Product>('/products', input),
    onSuccess: invalider,
  });
}

export function useModifierProduit() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ProductUpdate }) =>
      api.patch<Product>(`/products/${id}`, patch),
    onSuccess: invalider,
  });
}

export function useSupprimerProduit() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ supprime: boolean }>(`/products/${id}`),
    onSuccess: invalider,
  });
}

export function useReapprovisionner() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RestockInput }) =>
      api.post<Product>(`/products/${id}/restock`, input),
    onSuccess: invalider,
  });
}

export type { Paginated };
